// src/Components/Sliders/SliderTransparency.js

import debug from "debug";
const log = debug("app:sliderTransparency");

/**
 * Manages slider components to control transparency for various layers in the application.
 * @category Components
 * @subcategory Sidebar
 */
export class SliderTransparency {
  /**
   * Creates an instance of SliderTransparency.
   * @param {Object} experience - The experience object containing shared application resources.
   * @param {Object} experience.eventHub - The event hub for emitting and listening to application-wide events.
   */
  constructor(experience) {
    /**
     * The event hub for emitting and handling events related to slider changes.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * Stores the current values of sliders.
     * @type {Object<string, number>}
     */
    this.sliderValues = {};
  }


  cleanup() {
    console.log("SliderTransparency cleanup started...");
  
    // 1. Remove event listeners from sliders
    document.querySelectorAll(".slider-ranges").forEach((slider) => {
      slider.removeEventListener("input", this.handleSliderInput);
      slider.removeEventListener("mouseup", this.handleSliderMouseUp);
    });
  
    // 2. Remove event listener from eventHub
    if (this.handleSliderChange) {
      this.eventHub.off("sliderChange", this.handleSliderChange);
    }
  
    // 3. Clear slider values
    this.sliderValues = {};
  
    // 4. Clear object references
    this.eventHub = null;
  
    console.log("SliderTransparency cleanup completed.");
  }

  /**
   * @param {string} id - Slider element id
   * @param {number} value - Opacity 0–1
   * @param {number} layer - Layer id
   */
  _emitSliderChange(id, value, layer) {
    this.eventHub.emit("sliderChange", { id, value, layer });
  }

  /**
   * Maps transparency sliders to scene layers. Single source of truth for layer ids
   * (Housing multi-layer, GSG 50+40). Invoked on each input tick so all sliders update live consistently.
   * @param {string} id - Slider element id
   * @param {number} valueToSend - Opacity 0–1 (0.99 - raw/100)
   */
  emitTransparencyEventsForSlider(id, valueToSend) {
    if (id === "explodedView") return;

    switch (id) {
      case "TextBox":
        this.eventHub.emit("updateTextBoxAlpha", { value: valueToSend });
        localStorage.setItem("textboxAlpha", String(valueToSend));
        return;
      case "Housing":
        this._emitSliderChange(id, valueToSend, 20);
        this._emitSliderChange(id, valueToSend, 60);
        this._emitSliderChange(id, valueToSend, 37);
        this._emitSliderChange(id, valueToSend, 90);
        this._emitSliderChange(id, valueToSend, 34);
        log(`Applied opacity: ${valueToSend} for Housing layers 20, 60, 37, 90, 34`);
        return;
      case "GSG":
        this._emitSliderChange(id, valueToSend, 50);
        this._emitSliderChange(id, valueToSend, 40);
        log(`Applied opacity: ${valueToSend} for GSG layers 50, 40`);
        return;
      case "Shape":
        this._emitSliderChange(id, valueToSend, 30);
        break;
      case "Ctl":
        this._emitSliderChange(id, valueToSend, 10);
        break;
      case "Probe":
        this._emitSliderChange(id, valueToSend, 35);
        break;
      case "Walls":
        this._emitSliderChange(id, valueToSend, 33);
        break;
      case "Lids":
        this._emitSliderChange(id, valueToSend, 70);
        break;
      default:
        return;
    }
    log(`Applied opacity: ${valueToSend} for slider ${id}`);
  }

  /**
   * Initializes sliders: transparency uses `input` only so layer mapping stays consistent while dragging.
   */
  initSliders() {
    window.addEventListener("load", () => {
      localStorage.setItem("textboxAlpha", "1");
    });

    document.querySelectorAll(".slider-ranges").forEach((slider) => {
      slider.addEventListener("input", (event) => {
        const id = event.target.id;
        const valueToSend = 0.99 - event.target.value / 100;
        this.sliderValues[id] = valueToSend;
        log(`Slider ID: ${id}, Value: ${valueToSend}`);
        this.emitTransparencyEventsForSlider(id, valueToSend);
      });
    });
  }

  /**
   * Adds a listener for the `sliderChange` event to handle changes emitted by sliders.
   * Logs the slider ID, value, and associated layer on receiving the event.
   */
  addSliderChangeListener() {
    this.eventHub.on("sliderChange", (data) => {
      log(`Received event from slider with ID: ${data.id}`);
      log(`Value: ${data.value}, Layer: ${data.layer}`);
    });
  }
}
